import React, { useState } from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import GetAppOutlinedIcon from "@mui/icons-material/GetAppOutlined";
import { usePwaInstall } from "../hooks/usePwaInstall";

const BRAND_ORANGE = "#FF6A00";
const BRAND_MUTED = "#64748B";

/**
 * Renders an install affordance when the browser exposes `beforeinstallprompt`.
 * On iOS Safari (no install prompt API), shows a short "Add to Home Screen" hint.
 */
const PwaInstallPrompt = ({ variant = "button" }) => {
  const { canInstall, showIosHint, promptInstall } = usePwaInstall();
  const [isPrompting, setIsPrompting] = useState(false);

  if (!canInstall && !showIosHint) {
    return null;
  }

  if (showIosHint) {
    return (
      <Typography
        variant="caption"
        display="block"
        align="center"
        sx={{ mt: 2, color: BRAND_MUTED, lineHeight: 1.5, px: 1 }}
      >
        Install Thryiv HR: tap{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          Share
        </Box>{" "}
        then{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          Add to Home Screen
        </Box>
      </Typography>
    );
  }

  const handleInstall = async () => {
    setIsPrompting(true);
    try {
      await promptInstall();
    } finally {
      setIsPrompting(false);
    }
  };

  if (variant === "link") {
    return (
      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={handleInstall}
          disabled={isPrompting}
          aria-label="Install Thryiv HR app"
          sx={{
            color: BRAND_ORANGE,
            fontWeight: 600,
            fontSize: "0.875rem",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            border: "none",
            background: "none",
            fontFamily: "inherit",
            "&:hover": { color: "#E65A00" },
            "&.Mui-disabled": { color: BRAND_MUTED },
          }}
        >
          <GetAppOutlinedIcon sx={{ fontSize: "1.1rem" }} />
          Install Thryiv HR app
        </Link>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2.5 }}>
      <Button
        fullWidth
        variant="outlined"
        onClick={handleInstall}
        disabled={isPrompting}
        startIcon={<GetAppOutlinedIcon />}
        aria-label="Install Thryiv HR app"
        sx={{
          color: BRAND_ORANGE,
          borderColor: BRAND_ORANGE,
          borderRadius: "10px",
          py: 1.25,
          fontWeight: 600,
          fontSize: "0.875rem",
          textTransform: "none",
          "&:hover": {
            borderColor: "#E65A00",
            bgcolor: "rgba(255, 106, 0, 0.06)",
          },
          "&.Mui-disabled": {
            borderColor: "#E5E7EB",
            color: BRAND_MUTED,
          },
        }}
      >
        Install Thryiv HR app
      </Button>
    </Box>
  );
};

export default PwaInstallPrompt;
